import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import {
  CALC_ORDER_PRICE,
  EDIT_ORDER_POSITIONS,
  INSERT_ORDER_COMMENT,
  INSERT_USER_VIEWS_LOG,
  UPDATE_ORDER_VIA_ACTION,
} from '@/common/graphql/mutations/orders.mutations';
import {
  EXPRESS_NEW_ORDERS_MULTI_QUERY,
  GET_BLOCKED_DRIVERS,
  GET_DISTRICT_SCHEDULE_AND_WORKLOAD,
  GET_DRIVERS_WORKLOAD,
  GET_META,
  GET_ORDER_BY_ID_WITH_DRIVERS_WAREHOUSES,
  GET_ORDER_COMMENTS,
  GET_ORDER_HISTORY,
  GET_ORDER_LIST_BY_ID_WITH_DRIVERS_WAREHOUSES,
  GET_ORDERS,
  GET_PAYMENT_TYPES,
  GET_STATUSES,
  NEW_READY_AND_PICKED_ORDERS_MULTI_QUERY,
} from '@/common/graphql/queries/orders.queries';
import { getGraphQLClient } from '@/common/graphqlClient';
import { createMutationHook, createQueryHook } from '@/common/hooks/typedHookFactory';
import { QueryKeys } from '@/common/queryKeys';
import type {
  ExpressNewOrdersMultiQueryQuery,
  GetStatusesQueryVariables,
  NewReadyAndPickedOrdersMultiQueryQuery,
  Order_Bool_Exp,
} from '@/generated/graphql/graphql';
import type { AddressPatch, ClientPatch, OrderPatch } from '@/generated/graphql-types';
import {
  mapAddressUpdates,
  mapClientUpdates,
  mapOrderUpdates,
} from '@/views/orders/utils/orderPatchMappers';

export function statusesQueryUsesValidDepartment(where: Order_Bool_Exp): boolean {
  return typeof where.departmentId?._eq === 'number';
}

export const getStatusesQueryKeyParts = (
  variables: GetStatusesQueryVariables
): readonly [typeof QueryKeys.GET_STATUSES, string] =>
  [QueryKeys.GET_STATUSES, JSON.stringify(variables.ordersWhere)] as const;

export const useGetOrderComments = createQueryHook(GET_ORDER_COMMENTS, (variables) => [
  QueryKeys.GET_ORDER_COMMENTS,
  variables.orderId,
]);

export const useGetOrderHistory = createQueryHook(
  GET_ORDER_HISTORY,
  (variables) => [QueryKeys.GET_ORDER_HISTORY, variables.orderId, variables.offset],
  {
    enabled: (variables) => Boolean(variables.orderId),
  }
);

export const useGetOrders = createQueryHook(GET_ORDERS, (variables) => [
  QueryKeys.GET_ORDERS,
  JSON.stringify(variables),
  variables.limit,
  variables.offset,
]);

export const useGetMeta = createQueryHook(
  GET_META,
  (variables) => [QueryKeys.GET_META, variables.departmentId],
  {
    enabled: (variables) => Boolean(variables.departmentId),
  }
);

export const useGetBlockedDrivers = (
  variables: { departmentId: number },
  options?: { enabled?: boolean }
) => {
  const client = getGraphQLClient();

  return useQuery({
    queryKey: [QueryKeys.GET_BLOCKED_DRIVERS, variables.departmentId],
    queryFn: () => client.request(GET_BLOCKED_DRIVERS, variables),
    enabled: (options?.enabled ?? true) && Boolean(variables.departmentId),
  });
};

export const useGetStatuses = createQueryHook(
  GET_STATUSES,
  (variables: GetStatusesQueryVariables) => [...getStatusesQueryKeyParts(variables)],
  {
    enabled: (variables: GetStatusesQueryVariables) =>
      statusesQueryUsesValidDepartment(variables.ordersWhere),
    select: (data) => {
      const sortedStatuses = [...data.status].sort((a, b) => {
        const countA = a.orders_aggregate.aggregate?.count || 0;
        const countB = b.orders_aggregate.aggregate?.count || 0;
        return countB - countA;
      });

      return {
        ...data,
        status: sortedStatuses,
      };
    },
  }
);

export const useGetOrderByIdWithDriversWarehouses = createQueryHook(
  GET_ORDER_BY_ID_WITH_DRIVERS_WAREHOUSES,
  (variables) => [
    QueryKeys.GET_ORDER_BY_ID_WITH_DRIVERS_WAREHOUSES,
    variables.id,
    variables.departmentId,
  ],
  {
    enabled: (variables) => Boolean(variables.id && variables.departmentId),
  }
);

export const useGetOrderListByIdWithDriversWarehouses = createQueryHook(
  GET_ORDER_LIST_BY_ID_WITH_DRIVERS_WAREHOUSES,
  (variables) => [
    QueryKeys.GET_ORDER_LIST_BY_ID_WITH_DRIVERS_WAREHOUSES,
    Array.isArray(variables.ids) ? variables.ids.join(',') : variables.ids,
    variables.departmentId,
  ],
  {
    enabled: (variables) => Boolean(variables.ids && variables.departmentId),
  }
);

export const useGetDistrictScheduleAndWorkload = createQueryHook(
  GET_DISTRICT_SCHEDULE_AND_WORKLOAD,
  (variables) => [
    QueryKeys.GET_DISTRICT_SCHEDULE_AND_WORKLOAD,
    variables.districtId,
    variables.plannedDate,
    variables.nextDay,
  ],
  {
    enabled: (variables) =>
      Boolean(variables.districtId) && Boolean(variables.plannedDate) && Boolean(variables.nextDay),
  }
);

export const useGetDriversWorkload = createQueryHook(GET_DRIVERS_WORKLOAD, (variables) => [
  QueryKeys.GET_DRIVERS_WORKLOAD,
  variables.driverId,
  variables.districtId,
  variables.endOfWeek,
  variables.startOfWeek,
]);

export const useGetPaymentTypes = () => {
  const client = getGraphQLClient();

  return useQuery({
    queryKey: [QueryKeys.GET_PAYMENT_TYPES],
    queryFn: () => client.request(GET_PAYMENT_TYPES),
  });
};

export const useInsertOrderComment = createMutationHook(INSERT_ORDER_COMMENT, {
  invalidateQueries: [
    {
      queryKey: (variables) => [QueryKeys.GET_ORDER_COMMENTS, variables.orderId],
    },
  ],
});

export const useEditOrderPositions = createMutationHook(EDIT_ORDER_POSITIONS, {
  invalidateQueries: [
    {
      queryKey: (variables) => [
        QueryKeys.GET_ORDER_BY_ID_WITH_DRIVERS_WAREHOUSES,
        variables.orderID,
      ],
    },
  ],
});

export const useCalcOrderPrice = createMutationHook(CALC_ORDER_PRICE, {
  invalidateQueries: [
    {
      queryKey: (variables) => [QueryKeys.CALC_ORDER_PRICE, variables.orderID],
    },
  ],
});

export const useInsertUserViewsLog = createMutationHook(INSERT_USER_VIEWS_LOG);

export const useUpdateOrderFields = () => {
  const client = getGraphQLClient();
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: async (params: {
      orderId: number;
      addressId?: number;
      clientId?: number;
      orderUpdates?: OrderPatch;
      addressUpdates?: AddressPatch;
      clientUpdates?: ClientPatch;
    }) => {
      const orderPatch = mapOrderUpdates(params.orderUpdates);
      const clientPatch = mapClientUpdates(params.clientUpdates);
      const addressPatch = mapAddressUpdates(params.addressUpdates);

      if (
        Object.keys(orderPatch).length === 0 &&
        Object.keys(clientPatch).length === 0 &&
        Object.keys(addressPatch).length === 0
      ) {
        throw new Error('No updates to perform');
      }

      const response = await client.request(UPDATE_ORDER_VIA_ACTION, {
        orderId: params.orderId,
        order: Object.keys(orderPatch).length > 0 ? orderPatch : undefined,
        client: Object.keys(clientPatch).length > 0 ? clientPatch : undefined,
        address: Object.keys(addressPatch).length > 0 ? addressPatch : undefined,
      });

      return response;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_ORDER_BY_ID_WITH_DRIVERS_WAREHOUSES, variables.orderId],
      });
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QueryKeys.GET_ORDERS,
      });
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_DISTRICT_SCHEDULE_AND_WORKLOAD],
      });
      message.success('Order has been updated');

      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: [QueryKeys.GET_DISTRICT_SCHEDULE_AND_WORKLOAD],
        });
      }, 1000);
    },
    onError: (error) => {
      void message.error(`Error updating order: ${error}`);
    },
  });
};

type OrderLike = { id?: number | null; shortCode?: string | null };

const uniqOrders = <T extends OrderLike>(orders: T[]): T[] => {
  return Array.from(
    new Map(
      orders.map((order) => [order.id ?? order.shortCode ?? Math.random(), order] as const)
    ).values()
  );
};

export const useSubscribeToExpressNewMulti = (variables: { departmentIds: number[] }) => {
  const client = getGraphQLClient();

  return useQuery({
    queryKey: [QueryKeys.GET_ORDERS, 'express-new', variables.departmentIds],
    queryFn: async () => {
      const result = await client.request(EXPRESS_NEW_ORDERS_MULTI_QUERY, variables);
      return result.order || [];
    },
    refetchInterval: 60000, // 60 seconds
    enabled: variables.departmentIds.length > 0,
    select: (data) => {
      return uniqOrders(data as OrderLike[]) as ExpressNewOrdersMultiQueryQuery['order'];
    },
  });
};

export const useSubscribeToOrdersMulti = (variables: { departmentIds: number[] }) => {
  const client = getGraphQLClient();

  return useQuery({
    queryKey: [QueryKeys.GET_ORDERS, 'rappi-ready-picking', variables.departmentIds],
    queryFn: async () => {
      const result = await client.request(NEW_READY_AND_PICKED_ORDERS_MULTI_QUERY, variables);
      return result.order || [];
    },
    refetchInterval: 60000, // 60 seconds
    enabled: variables.departmentIds.length > 0,
    select: (data) => {
      return uniqOrders(data as OrderLike[]) as NewReadyAndPickedOrdersMultiQueryQuery['order'];
    },
  });
};
